import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Usuario } from '../../shared/models/usuario.model';

@Injectable({
  providedIn: 'root'
})
export class UsuariosService {
  private http = inject(HttpClient);

  private get serviceBaseURL(): string {
    return environment.URL_API;
  }

  /**
   * Obtiene todos los usuarios con filtros opcionales (API Platform /users)
   */
  findAllUsuarios(
    paginate: boolean,
    page: number,
    size: number,
    termino: string,
    multiSortMeta: any[] | undefined,
    filtersMetadata: any,
    accept: string = ''
  ): Observable<any> {
    let httpOptions: any = {};

    // Solo sobrescribir si se solicita explícitamente application/json
    if (accept !== undefined && accept !== null && accept !== '' && accept === 'json') {
      httpOptions = {
        headers: new HttpHeaders({ Accept: 'application/json' })
      };
    }

    let url = `${this.serviceBaseURL}/users?size=${size}&page=${page}`;

    if (paginate !== undefined && !paginate) {
      url = `${this.serviceBaseURL}/users?pagination=false`;
    }

    if (multiSortMeta !== undefined && multiSortMeta !== null && multiSortMeta.length > 0) {
      multiSortMeta.forEach((element: any) => {
        let direction = 'asc';
        if (element.order === -1) {
          direction = 'desc';
        }
        // La columna "username" es derivada (prefijo del email). Ordenar por email en backend.
        const sortField = element.field === 'username' ? 'email' : element.field;
        if (sortField) {
          url += `&_order[${sortField}]=${direction}`;
        }
      });
    }

    if (filtersMetadata !== undefined && filtersMetadata !== null) {
      Object.keys(filtersMetadata).forEach((property) => {
        const filter: any = filtersMetadata[property];

        if (property === 'global' && filter.value !== null && filter.value !== undefined && filter.value !== '') {
          // El filtro global (OR) en API Platform usa el query param "q"
          url += `&q=${encodeURIComponent(filter.value)}`;
          return;
        }

        if (filter !== undefined && filter instanceof Array) {
          filter.forEach((element: any) => {
            if (element.value !== null) {
              let value = element.value;
              const mappedProperty = property === 'username' ? 'email' : property;
              if (mappedProperty === 'enabled') {
                if (value === '1' || value === 1) value = 'true';
                if (value === '0' || value === 0) value = 'false';
              }
              url += `&${mappedProperty}=${encodeURIComponent(value)}`;
            }
          });
        }

        if (filter !== undefined && filter instanceof Object && !(filter instanceof Array)) {
          if (filter.value !== null) {
            let value = filter.value;
            const mappedProperty = property === 'username' ? 'email' : property;
            if (mappedProperty === 'enabled') {
              if (value === '1' || value === 1) value = 'true';
              if (value === '0' || value === 0) value = 'false';
            }
            url += `&${mappedProperty}=${encodeURIComponent(value)}`;
          }
        }
      });
    }

    return this.http.get(url, httpOptions).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Obtiene un usuario por ID
   */
  findUsuarioById(id: string): Observable<Usuario> {
    const url = `${this.serviceBaseURL}/users/${id}`;
    return this.http.get<Usuario>(url).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Alias de findUsuarioById
   */
  getUsuarioById(id: string): Observable<Usuario | undefined> {
    return this.findUsuarioById(id);
  }

  /**
   * Guarda un usuario (crea o actualiza)
   */
  guardarUsuario(usuario: Usuario): Observable<Usuario> {
    const id = usuario.id;
    const url = `${this.serviceBaseURL}/users`;

    if (usuario !== undefined && usuario.id !== undefined && Number(usuario.id) !== 0) {
      return this.actualizarUsuario(usuario);
    }

    return this.http.post(url, usuario).pipe(
      map((resp: any) => {
        // API Platform 4 puede devolver null en POST exitoso
        return (resp || { ...usuario, id }) as Usuario;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Actualiza la información de un usuario (PATCH merge-patch)
   */
  private actualizarUsuario(usuario: Usuario): Observable<Usuario> {
    const usuarioId = usuario.id;
    const url = `${this.serviceBaseURL}/users/${usuarioId}`;
    const payload: Partial<Usuario> = { ...usuario };
    delete (payload as any).id;

    const headers = new HttpHeaders({
      'Content-Type': 'application/merge-patch+json'
    });

    return this.http.patch(url, payload, { headers }).pipe(
      map((resp: any) => {
        // API Platform 4 puede devolver null en PATCH exitoso
        return (resp || { ...usuario, id: usuarioId }) as Usuario;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    console.error(`UsuariosService error: ${err.status} ${err.message}`, err);
    return throwError(() => err);
  }
}

